<template>
  <div class="reviews-list">
    <h4 class="mb-4">
      <i class="fas fa-comments text-primary me-2"></i>
      What Our Users Say
    </h4>
    
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
      <p class="mt-2 text-muted">Loading reviews...</p>
    </div>
    
    <!-- No Reviews State -->
    <div v-else-if="reviews.length === 0" class="text-center py-4">
      <i class="fas fa-comment-slash fa-3x text-muted mb-3"></i>
      <p class="text-muted">No reviews yet. Be the first to share your experience!</p>
    </div>
    
    <!-- Reviews Grid -->
    <div v-else class="reviews-grid">
      <div
        v-for="review in reviews"
        :key="review.id"
        class="review-card"
        :class="{ 'featured': review.is_featured }"
      >
        <!-- Review Header -->
        <div class="review-header">
          <div class="reviewer-info">
            <div class="reviewer-avatar">
              <i class="fas fa-user"></i>
            </div>
            <div class="reviewer-details">
              <h6 class="reviewer-name">{{ review.display_name }}</h6>
              <small class="review-date">{{ formatDate(review.created_at) }}</small>
            </div>
          </div>
          
          <!-- Rating -->
          <div v-if="review.rating" class="rating">
            <span
              v-for="star in 5"
              :key="star"
              class="star"
              :class="{ 'filled': star <= review.rating }"
            >
              <i class="fas fa-star"></i>
            </span>
          </div>
        </div>
        
        <!-- Review Content -->
        <div class="review-content">
          <p>{{ review.content }}</p>
        </div>
        
      </div>
    </div>
    
    <!-- Load More Button -->
    <div v-if="canShowMore && !loading" class="text-center mt-4">
      <button
        @click="loadMore"
        class="btn btn-outline-primary"
        :disabled="loadingMore"
      >
        <span v-if="loadingMore">
          <i class="fas fa-spinner fa-spin me-2"></i>
          Loading...
        </span>
        <span v-else>
          <i class="fas fa-chevron-down me-2"></i>
          {{ featured ? 'Read More' : 'Load More Reviews' }}
        </span>
      </button>
    </div>

    <!-- Show Less Button (for featured reviews when expanded) -->
    <div v-if="featured && expanded && !loading" class="text-center mt-4">
      <button
        @click="showLess"
        class="btn btn-outline-secondary"
      >
        <i class="fas fa-chevron-up me-2"></i>
        Show Less
      </button>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'ReviewsList',
  props: {
    featured: {
      type: Boolean,
      default: false
    },
    limit: {
      type: Number,
      default: 20
    }
  },
  data() {
    return {
      allReviews: [],
      loading: true,
      loadingMore: false,
      hasMore: true,
      page: 1,
      expanded: false
    }
  },
  computed: {
    // For featured mode: show limited reviews until expanded
    // For non-featured mode: show all loaded reviews
    reviews() {
      if (this.featured && !this.expanded) {
        return this.allReviews.slice(0, this.limit)
      }
      return this.allReviews
    },
    // Check if there are more reviews to show (for featured: client-side, for non-featured: API pagination)
    canShowMore() {
      if (this.featured) {
        return !this.expanded && this.allReviews.length > this.limit
      }
      return this.hasMore
    }
  },
  async mounted() {
    await this.loadReviews()
  },
  methods: {
    async loadReviews() {
      try {
        this.loading = true
        const endpoint = this.featured ? '/reviews/featured/' : '/reviews/recent/'
        const response = await api.get(endpoint)
        this.allReviews = response.data

        // If we got fewer results than the limit, there are no more
        this.hasMore = response.data.length >= this.limit

      } catch (error) {
        console.error('Error loading reviews:', error)
        this.allReviews = []
      } finally {
        this.loading = false
      }
    },

    async loadMore() {
      // For featured reviews: just expand to show all (client-side)
      if (this.featured) {
        this.expanded = true
        return
      }

      // For non-featured reviews: load more from API
      try {
        this.loadingMore = true
        this.page++

        const response = await api.get(`/reviews/?page=${this.page}`)
        const newReviews = response.data.results || response.data

        this.allReviews.push(...newReviews)
        this.hasMore = newReviews.length >= this.limit

      } catch (error) {
        console.error('Error loading more reviews:', error)
        this.page-- // Revert page increment on error
      } finally {
        this.loadingMore = false
      }
    },

    showLess() {
      this.expanded = false
    },

    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    refresh() {
      this.page = 1
      this.hasMore = true
      this.expanded = false
      this.loadReviews()
    }
  },
  watch: {
    featured() {
      this.refresh()
    }
  }
}
</script>

<style scoped>
.reviews-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.review-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  position: relative;
}

.review-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.review-card.featured {
  border: 2px solid #ffc107;
  background: linear-gradient(135deg, #fff9c4, #ffffff);
}

.featured-badge {
  position: absolute;
  top: -8px;
  right: 1rem;
  background: #ffc107;
  color: #212529;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.reviewer-info {
  display: flex;
  align-items: center;
}

.reviewer-avatar {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 0.75rem;
}

.reviewer-details {
  flex-grow: 1;
}

.reviewer-name {
  margin: 0;
  font-weight: 600;
  color: #212529;
}

.review-date {
  color: #6c757d;
}

.rating {
  display: flex;
  align-items: center;
}

.star {
  font-size: 1rem;
  color: #ddd;
  margin-right: 0.125rem;
}

.star.filled {
  color: #ffc107;
}

.review-content {
  margin-bottom: 1rem;
}

.review-content p {
  margin: 0;
  line-height: 1.6;
  color: #495057;
}

.review-footer {
  border-top: 1px solid #e9ecef;
  padding-top: 0.75rem;
}

.btn-outline-primary {
  border-radius: 8px;
  padding: 0.75rem 2rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-outline-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
}

@media (max-width: 768px) {
  .reviews-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin: 0 1rem 2rem;
  }
  
  .review-card {
    padding: 1rem;
  }
  
  .reviewer-avatar {
    width: 32px;
    height: 32px;
    margin-right: 0.5rem;
  }
  
  .review-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}

@media (max-width: 480px) {
  .reviews-grid {
    margin: 0 0.5rem 2rem;
  }
}
</style>